import { PDFDocument, rgb } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import { useAuth } from '../../../contexts/AuthContext';

// Utility function to fix unquoted numeric keys in a JSON string
function fixToJSON(str) {
  return str.replace(/(\{|,)\s*([0-9]+)\s*:/g, '$1 "$2":');
}

/**
 * Creates a PDF document for the plan of study.
 *
 * @param {Object} finalizedOrganizedCourses - The plan data organized by year and term.
 * @param {Object} currentUser - The current user's Firestore document data.
 * @param {string} fetchedTerms - A JSON string representing the dynamic terms (e.g. '{"1":"Fall","2":"Spring","3":"Summer"}').
 * @returns {Promise<Uint8Array>} - The generated PDF document as bytes.
 */
export const createPDFDocument = async (finalizedOrganizedCourses, currentUser, fetchedTerms) => {
  try {
    // Process fetchedTerms into a dynamic term map
    let termMap = {};
    try {
      const validJSONString = fixToJSON(fetchedTerms);
      const parsedTerms = JSON.parse(validJSONString);
      Object.keys(parsedTerms).forEach(key => {
        termMap[key] = parsedTerms[key];
      });
    } catch (error) {
      console.error('Error parsing fetchedTerms:', error);
    }
    const sortedTermKeys = Object.keys(termMap).sort((a, b) => Number(a) - Number(b));
    // dynamicTerms is an array of friendly term names (e.g. ['Fall', 'Spring', 'Summer'])
    const dynamicTerms = sortedTermKeys.map(key => termMap[key]);
    console.log('Dynamic Term Map:', termMap);
    console.log('Sorted Term Keys:', sortedTermKeys);
    console.log('Dynamic Terms:', dynamicTerms);

    // Function to fetch and embed assets (images or fonts)
    async function fetchAndEmbedAsset(pdfDoc, url, type) {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch ${type} from ${url}: ${response.statusText}`);
        }
        const assetBytes = await response.arrayBuffer();
        if (type === 'image') {
          return pdfDoc.embedPng(assetBytes);
        } else if (type === 'font') {
          return pdfDoc.embedFont(assetBytes);
        }
      } catch (error) {
        console.error(`Error fetching and embedding ${type}:`, error);
        throw error;
      }
    }

    // Function to truncate text with ellipsis if it exceeds maxWidth
    const truncateWithEllipsis = (text, font, fontSize, maxWidth) => {
      const ellipsis = '...';
      const ellipsisWidth = font.widthOfTextAtSize(ellipsis, fontSize);
      let currentWidth = font.widthOfTextAtSize(text, fontSize);
      if (currentWidth <= maxWidth) {
        return text;
      }
      let truncatedText = text;
      while (truncatedText.length > 0) {
        truncatedText = truncatedText.slice(0, -1);
        currentWidth = font.widthOfTextAtSize(truncatedText, fontSize) + ellipsisWidth;
        if (currentWidth <= maxWidth) {
          break;
        }
      }
      return truncatedText + ellipsis;
    };

    // Function to wrap text into multiple lines based on maxWidth
    const wrapText = (text, font, fontSize, maxWidth) => {
      const words = text.split(' ');
      const lines = [];
      let currentLine = '';
      words.forEach((word) => {
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        const testLineWidth = font.widthOfTextAtSize(testLine, fontSize);
        if (testLineWidth > maxWidth && currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      });
      if (currentLine) {
        lines.push(currentLine);
      }
      return lines;
    };

    // Helper function to estimate the height required for a semester's content
    const estimateSemesterHeight = (courses, lineHeight, bodyFontSize) => {
      const numCourses = courses.length;
      const semesterHeaderHeight = lineHeight;
      const coursesHeight = numCourses * lineHeight;
      const totalCreditsHeight = lineHeight + 10; // For the 'Total Credits' line
      const semesterHeight = semesterHeaderHeight + coursesHeight + totalCreditsHeight;
      return semesterHeight;
    };

    // Create a new PDF document
    const pdfDoc = await PDFDocument.create();
    pdfDoc.registerFontkit(fontkit);

    // Load fonts and logo image
    const robotoFont = await fetchAndEmbedAsset(
      pdfDoc,
      `${process.env.PUBLIC_URL}/fonts/Roboto-Regular.ttf`,
      'font'
    );
    const robotoBoldFont = await fetchAndEmbedAsset(
      pdfDoc,
      `${process.env.PUBLIC_URL}/fonts/Roboto-Bold.ttf`,
      'font'
    );
    const logoImage = await fetchAndEmbedAsset(
      pdfDoc,
      `${process.env.PUBLIC_URL}/edviseicon.png`,
      'image'
    );

    // Get the active plan's number of years
    const activeTab = currentUser?.activeTab || '';
    const numYears = currentUser?.schedules?.[activeTab]?.numYears || 4; // Default to 4 years

    // Layout constants
    const marginTop = 20;
    const marginBottom = 50;
    const marginLeft = 20;
    const marginRight = 20;
    const lineHeight = 15;
    const gapBetweenColumns = 20;
    // Calculate column width based on the number of dynamic terms
    const columnWidth = (612 - marginLeft - marginRight - (dynamicTerms.length - 1) * gapBetweenColumns) / dynamicTerms.length;
    const headerFontSize = 18;
    const subHeaderFontSize = 14;
    const bodyFontSize = 10;
    const headerColor = rgb(0, 0, 0);
    const subHeaderColor = rgb(0, 0, 0);
    const bodyTextColor = rgb(0, 0, 0);

    // Create the initial page
    let page = pdfDoc.addPage([612, 792]);
    let { width, height } = page.getSize();
    let yPosition = height - marginTop;

    // Define fixed areas for the header
    const leftSectionWidth = 250;
    const rightSectionWidth = width - marginLeft - marginRight - leftSectionWidth;

    // Function to add a new page and redraw the header
    const addNewPage = () => {
      page = pdfDoc.addPage([612, 792]);
      width = page.getSize().width;
      height = page.getSize().height;
      yPosition = height - marginTop;
      drawHeader();
    };

    // Function to draw the header on each page
    const drawHeader = () => {
      page.drawRectangle({
        x: 0,
        y: height - 80,
        width: width,
        height: 80,
        color: rgb(0.95, 0.95, 0.95),
      });
      const logoDims = logoImage.scale(0.03);
      page.drawImage(logoImage, {
        x: marginLeft,
        y: height - 70,
        width: logoDims.width,
        height: logoDims.height,
      });
      page.drawText(`${numYears}-Year Plan of Study`, {
        x: marginLeft + logoDims.width + 10,
        y: height - 50,
        size: headerFontSize,
        font: robotoBoldFont,
        color: headerColor,
      });
      const studentInfoStartX = marginLeft + leftSectionWidth;
      const maxTotalInfoWidth = rightSectionWidth;
      const studentInfoLabels = ['Student Name:', 'Major:', 'Minor:', 'Plan Name:'];
      const studentInfoValues = [
        `${currentUser?.firstName ?? 'N/A'} ${currentUser?.lastName ?? 'N/A'}`,
        `${currentUser?.surveyAnswers?.Majors?.join(', ') ?? 'N/A'}`,
        `${currentUser?.surveyAnswers?.Minors?.join(', ') ?? 'N/A'}`,
        `${currentUser?.activeTab ?? 'N/A'}`,
      ];
      const infoStartY = height - 20;
      const infoSpacing = 16;
      const smallSpace = 2;
      studentInfoLabels.forEach((label, index) => {
        const currentY = infoStartY - index * infoSpacing;
        const truncatedLabel = truncateWithEllipsis(label, robotoBoldFont, bodyFontSize, maxTotalInfoWidth / 2);
        const labelWidth = robotoBoldFont.widthOfTextAtSize(truncatedLabel, bodyFontSize);
        const valueX = studentInfoStartX + labelWidth + smallSpace;
        const maxValueWidth = maxTotalInfoWidth - labelWidth - smallSpace;
        const truncatedValue = truncateWithEllipsis(
          studentInfoValues[index],
          robotoFont,
          bodyFontSize,
          maxValueWidth
        );
        page.drawText(truncatedLabel, {
          x: studentInfoStartX,
          y: currentY,
          size: bodyFontSize,
          font: robotoBoldFont,
          color: bodyTextColor,
        });
        page.drawText(truncatedValue, {
          x: valueX,
          y: currentY,
          size: bodyFontSize,
          font: robotoFont,
          color: bodyTextColor,
        });
      });
      yPosition = height - 100;
    };

    drawHeader();

    // Build coursesByYear using dynamic terms rather than fixed 'S1' and 'S2'
    const coursesByYear = {};
    for (let i = 1; i <= numYears; i++) {
      const yearKey = `year${i}`;
      coursesByYear[yearKey] = {};
      dynamicTerms.forEach(term => {
        coursesByYear[yearKey][term] = finalizedOrganizedCourses?.[yearKey]?.[term] || [];
      });
    }

    const startYear = Number(currentUser?.surveyAnswers?.SchoolStart?.year || new Date().getFullYear());

    // Iterate over each year
    for (let yearIndex = 1; yearIndex <= numYears; yearIndex++) {
      const yearKey = `year${yearIndex}`;
      const yearTitle = `Year ${yearIndex} (${startYear + yearIndex - 1} - ${startYear + yearIndex})`;

      // For each dynamic term in the year, estimate the required height and determine the maximum
      let maxSemesterHeight = 0;
      dynamicTerms.forEach(term => {
        const semesterCourses = coursesByYear[yearKey][term];
        const semesterHeight = estimateSemesterHeight(semesterCourses, lineHeight, bodyFontSize);
        if (semesterHeight > maxSemesterHeight) {
          maxSemesterHeight = semesterHeight;
        }
      });

      const yearTitleHeight = lineHeight + 20;
      const yearExtraSpacing = 20;
      const totalYearHeight = yearTitleHeight + maxSemesterHeight + yearExtraSpacing;

      if (yPosition - totalYearHeight < marginBottom) {
        addNewPage();
      }

      // Draw the year title and a separator line
      page.drawText(yearTitle, {
        x: marginLeft,
        y: yPosition,
        size: subHeaderFontSize,
        font: robotoBoldFont,
        color: subHeaderColor,
      });
      yPosition -= lineHeight;
      page.drawLine({
        start: { x: marginLeft, y: yPosition },
        end: { x: width - marginRight, y: yPosition },
        thickness: 1,
        color: rgb(0.8, 0.8, 0.8),
      });
      yPosition -= 20;

      // Draw each dynamic term column
      const semesterYPositions = [];
      dynamicTerms.forEach((term, index) => {
        const xPos = marginLeft + index * (columnWidth + gapBetweenColumns);
        // Helper function to draw semester content
        const drawSemester = (semesterName, courses, startX) => {
          let currentY = yPosition;
          page.drawText(`${semesterName} Semester`, {
            x: startX,
            y: currentY,
            size: subHeaderFontSize,
            font: robotoBoldFont,
            color: headerColor,
          });
          currentY -= lineHeight;
          let totalCredits = 0;
          courses.forEach(course => {
            totalCredits += course.Credits;
            const courseText = `${course.courseInfo_courseNumber}: ${course.courseInfo_courseName} (${course.Credits} Credits)`;
            const truncatedCourseText = truncateWithEllipsis(courseText, robotoFont, bodyFontSize, columnWidth);
            if (currentY - lineHeight < marginBottom) {
              addNewPage();
              currentY = yPosition;
              page.drawText(`${semesterName} Semester`, {
                x: startX,
                y: currentY,
                size: subHeaderFontSize,
                font: robotoBoldFont,
                color: headerColor,
              });
              currentY -= lineHeight;
            }
            page.drawText(truncatedCourseText, {
              x: startX,
              y: currentY,
              size: bodyFontSize,
              font: robotoFont,
              color: bodyTextColor,
            });
            currentY -= lineHeight;
          });
          page.drawText(`Total Credits: ${totalCredits}`, {
            x: startX,
            y: currentY - 10,
            size: bodyFontSize,
            font: robotoBoldFont,
            color: bodyTextColor,
          });
          currentY -= lineHeight + 10;
          return currentY;
        };

        const semesterCourses = coursesByYear[yearKey][term];
        const semesterYPos = drawSemester(term, semesterCourses, xPos);
        semesterYPositions.push(semesterYPos);
      });

      // Update yPosition based on the lowest column
      yPosition = Math.min(...semesterYPositions) - 20;
      if (yPosition < marginBottom) {
        addNewPage();
      }
    }

    // Add page numbers to each page
    const totalPages = pdfDoc.getPageCount();
    pdfDoc.getPages().forEach((page, index) => {
      const { width } = page.getSize();
      page.drawText(`Page ${index + 1} of ${totalPages}`, {
        x: width / 2 - 30,
        y: marginBottom - 20,
        size: 8,
        font: robotoFont,
        color: rgb(0, 0, 0),
      });
    });

    const pdfBytes = await pdfDoc.save();
    return pdfBytes;
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};
